import { DndContext, MeasuringStrategy } from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import * as React from 'react'
import { PlannedPost as IPlannedPost } from 'interfaces'
import { ScrollListener } from 'components/ScrollListener'
import { PostGroupHeader } from '../PostGroup'
import { DraggablePlannedPost } from 'components/Posts/PlannedPost'
import EmptyPostSlot from 'components/Posts/EmptyPostSlot'
import { useDispatch } from 'react-redux'
import { message } from 'services/snackbar'
import BulkPostActions, { BulkPostActionsProps } from 'routes/publishing/components/BulkActionsMenus/BulkPostActions'
import styles from './PlannedPostsList.pcss'
import { isElementInViewport } from 'utils/dom'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { reorder } from 'shared'
import { GroupedVirtuoso, ScrollSeekPlaceholderProps } from 'react-virtuoso'
import { ScrollSeekPlaceholder } from 'components/VirtualizedList'

const SCROLL_TRESHOLD = 350
const POST_DROP_ERROR_MESSAGE_TIMEOUT = 4000

export interface PostGroupData {
  posts: IPlannedPost[]
  index?: number
  key?: string
  weekday: string
  postsStartIndex: number
}

export interface PlannedPostsListProps {
  posts: IPlannedPost[]
  selectedPosts: { [id: string]: boolean } | 'all'
  activePreviewPostId?: string
  bulkActionsProps: BulkPostActionsProps
  bucketsView?: boolean
  skipReorderChecks?: boolean
  hideMoveUpDown?: boolean
  onLoadNextPage: () => void
  onRecycleChange: (postId: string, currentValue: number | boolean | Date, forceRefresh?: boolean) => void
  onTogglePostSelected: (id: string) => void
  onEditPost: (post: IPlannedPost) => void
  onCopyPost: (post: IPlannedPost) => void
  onDeletePost: (id: string) => void
  onShareNow?: (post: IPlannedPost) => void
  onMovePost?: (post: IPlannedPost, position: 'top' | 'bottom' | 'up' | 'down') => void
  onPostOrderChanged?: (movedPost: IPlannedPost, targetPost: IPlannedPost) => void
  onMediaPreviewBtnClick: (post: IPlannedPost) => void
  onPostClick: (post: IPlannedPost) => void
  renderPosts?: (postGroups: Record<string, PostGroupData>) => React.ReactNode
}

export const PlannedPostsList = React.memo((props: PlannedPostsListProps) => {
  const dispatch = useDispatch()
  const { skipReorderChecks, onPostOrderChanged } = props
  const [movingPost, setMovingPost] = React.useState<IPlannedPost | null>(null)
  const scrollElementRef = React.useRef<HTMLElement | undefined>(undefined)
  const [order, setOrder] = React.useState(props.posts.map(p => p.id))

  React.useEffect(() => {
    setOrder(props.posts.map(p => p.id))
  }, [props.posts])

  React.useLayoutEffect(() => {
    scrollElementRef.current = document.querySelector('[data-test="main"]') as HTMLElement
  }, [])

  React.useLayoutEffect(() => {
    const scrollOffsetTop = 120
    if (props.activePreviewPostId) {
      const element = document.querySelector(`[data-post-id="${props.activePreviewPostId}"]`) as HTMLElement
      if (element && !isElementInViewport(element, scrollOffsetTop)) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [props.activePreviewPostId])

  const { postGroups, groupsArray, groupCounts } = React.useMemo(() => {
    const grouped = props.posts.reduce((groups: { [title: string]: PostGroupData }, post: IPlannedPost, index) => {
      const group = groups[post.weekday]
      if (!group) {
        groups[post.weekday] = {
          posts: [post],
          key: post.weekday,
          weekday: post.weekday,
          postsStartIndex: index
        }
      } else {
        group.posts.push(post)
      }
      return groups
    }, {})

    Object.keys(grouped).forEach((weekday, index) => {
      const posts = grouped[weekday].posts.map((p, index) => {
        return {
          postId: p.id,
          profileId: p.profileId,
          autoPost: Boolean((p as IPlannedPost).autoPost),
          order: (p as IPlannedPost).autoPostOrder,
          index
        }
      })

      grouped[weekday].key = JSON.stringify({ posts, weekday, groupIndex: index })
      grouped[weekday].index = index
    })

    const values = Object.values(grouped)
    return {
      postGroups: grouped,
      groupsArray: values,
      groupCounts: values.map(group => group.posts.length)
    }
  }, [props.posts])

  const onDragStart = (data: any) => {
    const post = props.posts.find(p => p.id === data.active.id)
    if (post) {
      setMovingPost(post)
    }
  }

  const onDragEnd = React.useCallback((data: any) => {
    setMovingPost(null)
    const { active, over } = data
    if (active.id === over?.id) {
      return
    }
    const draggedPost = active.data?.current?.post as IPlannedPost
    const targetPost = over?.data?.current?.post as IPlannedPost | null

    if (!targetPost) {
      return
    }

    // EXPL: Check if post is dropped at a valid slot:
    //  - not on a scheduled post (not autoPost)
    //  - not on post of different profile
    //  - slots buckets not matching

    const bucketsMatching = draggedPost.bucketId === targetPost.bucketId
    const dropInvalid = !targetPost.autoPost || targetPost.profileId !== draggedPost.profileId || !bucketsMatching

    if (!skipReorderChecks && dropInvalid) {
      dispatch(message(
        `Oops!.. You can't drop your post there. Please drag it to a valid time slot outlined in green.`,
        'warning',
        POST_DROP_ERROR_MESSAGE_TIMEOUT
      ))
      return
    }

    const oldIndex = order.indexOf(active.id)
    const newIndex = order.indexOf(over.id)
    const reordered = reorder(order, oldIndex, newIndex)
    setOrder(reordered)

    if (onPostOrderChanged) {
      onPostOrderChanged(draggedPost, targetPost)
    }
  }, [skipReorderChecks, onPostOrderChanged, dispatch, order])

  if (props.posts.length === 0) {
    return null
  }

  return (
    <DndContext
      modifiers={[restrictToVerticalAxis]}
      layoutMeasuring={{ strategy: MeasuringStrategy.Always }}
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
    >
      <div className={styles.wrapper} data-test="post-list">
        <div className={styles.actions}>
          <BulkPostActions {...props.bulkActionsProps} />
        </div>
        <ScrollListener
          scrollElement={scrollElementRef.current}
          emitTreshold={SCROLL_TRESHOLD}
          onScroll={props.onLoadNextPage}
        >
          <div>
            <SortableContext items={order} strategy={rectSortingStrategy}>
              {props.renderPosts && props.renderPosts(postGroups)}
              {!props.renderPosts && (
                <GroupedVirtuoso
                  increaseViewportBy={2000}
                  overscan={450}
                  customScrollParent={scrollElementRef.current}
                  groupCounts={groupCounts}
                  components={{
                    ScrollSeekPlaceholder: PostPlaceholder
                  }}
                  scrollSeekConfiguration={{
                    /* eslint-disable no-magic-numbers */
                    enter: (velocity) => Math.abs(velocity) > 800,
                    exit: (velocity) => Math.abs(velocity) < 80
                    /* eslint-enable no-magic-numbers */
                  }}
                  groupContent={(index) => <PostGroupHeader title={groupsArray[index].weekday} />}
                  itemContent={(flatIndex) => {
                    const post = props.posts[flatIndex]
                    if (!post) {
                      return null
                    }
                    if (post.isEmpty) {
                      const className = movingPost ? `${styles['empty-slot']} ${styles['slot-disabled']}` : styles['empty-slot']
                      return (
                        <EmptyPostSlot
                          timeString={post.timeString}
                          baseTime={post.baseTime}
                          profile={{
                            type: post.network,
                            image: post.profilePictureUrl,
                            name: post.name as string
                          }}
                          bucket={post.bucket}
                          className={className}
                          key={post.id}
                        />
                      )
                    }

                    let className = props.activePreviewPostId === post.id ? styles.highlight : ''
                    if (
                      !post.isVirtual
                      && post.autoPost
                      && movingPost
                      && movingPost.profileId === post.profileId
                      && movingPost.id !== post.id
                      && movingPost.bucketId === post.bucketId
                    ) {
                      className = (movingPost as IPlannedPost).autoPostOrder < (post as IPlannedPost).autoPostOrder
                        ? styles['drop-below']
                        : styles['drop-above']
                    }

                    return (
                      <DraggablePlannedPost
                        key={post.id}
                        selected={props.selectedPosts === 'all' || !!props.selectedPosts[post.id]}
                        post={post}
                        className={className}
                        withBucket={props.bucketsView}
                        hideMoveUpDown={props.hideMoveUpDown}
                        onPostClick={props.onPostClick}
                        onEdit={props.onEditPost}
                        onCopy={props.onCopyPost}
                        onDelete={props.onDeletePost}
                        onMovePost={props.onMovePost}
                        onRecycleChange={props.onRecycleChange}
                        onToggleSelected={props.onTogglePostSelected}
                        onMediaPreviewBtnClick={props.onMediaPreviewBtnClick}
                        onShareNow={props.onShareNow}
                      />
                    )
                  }}
                />
              )}
            </SortableContext>
          </div>
        </ScrollListener>
      </div>
    </DndContext>
  )
})

function PostPlaceholder(props: ScrollSeekPlaceholderProps) {
  return (
    <ScrollSeekPlaceholder
      {...props}
      className={`${styles.placeholder} ${styles[props.type]}`}
      containerClassName={styles.placeholderContainer}
    />
  )
}

export default PlannedPostsList
